import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastProvider } from './components/Toast';
import Header from './components/Header';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import DashboardVecino from './pages/DashboardVecino';
import Incidencias from './pages/IncidenciasRedisenio';
import IncidenciaDetalle from './pages/IncidenciaDetalle';
import NuevaIncidencia from './pages/NuevaIncidencia';
import Usuarios from './pages/Usuarios';
import TablonAnuncios from './pages/TablonAnuncios';
import NuevoAnuncio from './pages/NuevoAnuncio';
import AnuncioDetalle from './pages/AnuncioDetalle';
import Proveedores from './pages/Proveedores';
import Documentos from './pages/Documentos';
import Permisos from './pages/Permisos';
import ComunidadDetalle from './pages/ComunidadDetalle';
import './styles/App.css';
import Comunidades from './pages/Comunidades';
import Reservas from './pages/Reservas';
import Tickets from './pages/Tickets';
import Ajustes from './pages/Ajustes';
import Invitaciones from './pages/Invitaciones';
import Privacidad from './pages/Privacidad';
import EliminarCuenta from './pages/EliminarCuenta';
import Seguridad from './pages/Seguridad';
import GestionAdmins from './pages/GestionAdmins';
import Fichajes from './pages/Fichajes';
import NotificacionesPush from './pages/NotificacionesPush';
import { registrarPushNotifications } from './services/pushNotifications';
import { getMe, logout as apiLogout } from './services/api';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      // Hay datos de usuario en cache: verificar con el servidor que la sesión (cookie) sigue válida
      getMe()
        .then(res => {
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));
        })
        .catch(() => {
          // Cookie expirada o inválida — limpiar
          localStorage.removeItem('user');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    registrarPushNotifications();
  };

  useEffect(() => {
    if (user) {
      registrarPushNotifications();
    }
  }, [user]);

  const handleLogout = () => {
    apiLogout().catch(() => {}).finally(() => {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      setUser(null);
    });
  };

  function skeletonStyle(width, height, borderRadius) {
    return {
      width, height, borderRadius,
      background: 'linear-gradient(90deg, #e8ebf2 25%, #d4d8e2 50%, #e8ebf2 75%)',
      backgroundSize: '200% 100%',
      animation: 'skeletonShimmer 1.4s infinite',
      flexShrink: 0
    };
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#E8EBF2', display: 'flex', flexDirection: 'column' }}>
        {/* Top bar skeleton */}
        <div style={{ height: '60px', background: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', padding: '0 20px', gap: '12px' }}>
          <div style={skeletonStyle('32px', '32px', '50%')} />
          <div style={skeletonStyle('120px', '18px', '6px')} />
          <div style={{ flex: 1 }} />
          <div style={skeletonStyle('32px', '32px', '50%')} />
          <div style={skeletonStyle('32px', '32px', '50%')} />
        </div>
        {/* Content skeleton */}
        <div style={{ flex: 1, padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={skeletonStyle('200px', '28px', '8px')} />
          <div style={skeletonStyle('100%', '120px', '16px')} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div style={skeletonStyle('100%', '88px', '16px')} />
            <div style={skeletonStyle('100%', '88px', '16px')} />
          </div>
          <div style={skeletonStyle('100%', '88px', '16px')} />
          <div style={skeletonStyle('100%', '88px', '16px')} />
        </div>
        {/* Bottom tabs skeleton */}
        <div style={{ height: '64px', background: '#fff', borderTop: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-around', padding: '0 12px' }}>
          {[1,2,3,4].map(i => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
              <div style={skeletonStyle('24px', '24px', '6px')} />
              <div style={skeletonStyle('36px', '10px', '4px')} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <ToastProvider>
      <Router>
        <AppRoutes user={user} handleLogin={handleLogin} handleLogout={handleLogout} />
      </Router>
    </ToastProvider>
  );
}

function AppRoutes({ user, handleLogin, handleLogout }) {
  return (
    <div className={`App ${user ? 'app-layout' : ''}`}>
      {user && <Header user={user} onLogout={handleLogout} />}
      <div className={user ? 'main-content' : ''}>
        <ErrorBoundary>
          <div className="page-transition">
          <Routes>
          <Route
            path="/login"
            element={user ? <Navigate to="/dashboard" /> : <Login onLogin={handleLogin} />}
          />
          <Route
            path="/register"
            element={user ? <Navigate to="/dashboard" /> : <Register onLogin={handleLogin} />}
          />
          <Route
            path="/registro"
            element={user ? <Navigate to="/dashboard" /> : <Register onLogin={handleLogin} />}
          />
          <Route
            path="/forgot-password"
            element={user ? <Navigate to="/dashboard" /> : <ForgotPassword />}
          />
          <Route
            path="/reset-password"
            element={user ? <Navigate to="/dashboard" /> : <ResetPassword />}
          />

          <Route 
            path="/dashboard" 
            element={user ? (
              user.rol === 'proveedor' 
                ? <Navigate to="/incidencias" /> 
                : user.rol === 'conserje'
                  ? <Navigate to="/fichajes" />
                  : <DashboardVecino user={user} />
            ) : <Navigate to="/login" />} 
          />
          <Route 
            path="/incidencias" 
            element={user ? <Incidencias user={user} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/incidencias/nueva" 
            element={user ? (user.rol === 'proveedor' ? <Navigate to="/incidencias" /> : <NuevaIncidencia user={user} />) : <Navigate to="/login" />} 
          />
          <Route 
            path="/incidencias/:id" 
            element={user ? <IncidenciaDetalle user={user} /> : <Navigate to="/login" />} 
          />
          <Route 
            path="/usuarios" 
            element={user?.rol === 'administrador' || user?.rol === 'presidente' ? <Usuarios user={user} /> : <Navigate to={user?.rol === 'conserje' ? "/fichajes" : user?.rol === 'proveedor' ? "/incidencias" : "/dashboard"} />} 
          />
          <Route 
            path="/tablon" 
            element={user ? (user.rol === 'proveedor' || user.rol === 'conserje' ? <Navigate to={user.rol === 'conserje' ? "/fichajes" : "/incidencias"} /> : <TablonAnuncios user={user} />) : <Navigate to="/login" />} 
          />
          <Route 
            path="/tablon/nuevo" 
            element={user?.rol === 'administrador' || user?.rol === 'presidente' ? <NuevoAnuncio /> : <Navigate to={user?.rol === 'conserje' ? "/fichajes" : user?.rol === 'proveedor' ? "/incidencias" : "/tablon"} />} 
          />
          <Route 
            path="/tablon/:id" 
            element={user ? (user.rol === 'proveedor' ? <Navigate to="/incidencias" /> : <AnuncioDetalle user={user} />) : <Navigate to="/login" />} 
          />
           <Route
            path="/comunidades"
            element={user ? (user.rol === 'proveedor' || user.rol === 'conserje' ? <Navigate to={user.rol === 'conserje' ? "/fichajes" : "/incidencias"} /> : <Comunidades user={user} />) : <Navigate to="/login" />}
          />
          <Route
            path="/comunidades/:id"
            element={user ? (user.rol === 'proveedor' || user.rol === 'conserje' ? <Navigate to={user.rol === 'conserje' ? "/fichajes" : "/incidencias"} /> : <ComunidadDetalle />) : <Navigate to="/login" />}
          />
          <Route
            path="/proveedores"
            element={user?.rol === 'administrador' || user?.rol === 'presidente' ? <Proveedores /> : <Navigate to={user?.rol === 'conserje' ? "/fichajes" : user?.rol === 'proveedor' ? "/incidencias" : "/dashboard"} />}
          />
          <Route
            path="/documentos"
            element={user ? (user.rol === 'proveedor' || user.rol === 'conserje' ? <Navigate to={user.rol === 'conserje' ? "/fichajes" : "/incidencias"} /> : <Documentos />) : <Navigate to="/login" />}
          />
          <Route
            path="/permisos"
            element={user?.rol === 'administrador' || user?.rol === 'presidente' ? <Permisos /> : <Navigate to={user?.rol === 'conserje' ? "/fichajes" : user?.rol === 'proveedor' ? "/incidencias" : "/dashboard"} />}
          />
          <Route
            path="/notificaciones-push"
            element={user?.rol === 'administrador' || user?.rol === 'presidente' ? <NotificacionesPush user={user} /> : <Navigate to={user?.rol === 'conserje' ? "/fichajes" : user?.rol === 'proveedor' ? "/incidencias" : "/dashboard"} />}
          />
          <Route
            path="/reservas"
            element={user ? (user.rol === 'proveedor' || user.rol === 'conserje' ? <Navigate to={user.rol === 'conserje' ? "/fichajes" : "/incidencias"} /> : <Reservas user={user} />) : <Navigate to="/login" />}
          />
          <Route
            path="/tickets"
            element={user ? <Tickets user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/ajustes"
            element={user ? <Ajustes /> : <Navigate to="/login" />}
          />
          <Route
            path="/invitaciones"
            element={user?.rol === 'administrador' || user?.rol === 'presidente' ? <Invitaciones user={user} /> : <Navigate to={user?.rol === 'conserje' ? "/fichajes" : user?.rol === 'proveedor' ? "/incidencias" : "/dashboard"} />}
          />

          <Route path="/privacidad" element={<Privacidad />} />
          <Route path="/eliminar-cuenta" element={<EliminarCuenta />} />
          <Route path="/seguridad" element={<Seguridad />} />
          <Route path="/gestion-admins" element={
            user?.is_superadmin ? <GestionAdmins user={user} /> : <Navigate to="/dashboard" />
          } />
          <Route path="/fichajes" element={user ? (['administrador', 'presidente', 'conserje'].includes(user.rol) ? <Fichajes user={user} /> : <Navigate to="/dashboard" />) : <Navigate to="/login" />} />
          <Route path="/" element={<Navigate to={user ? (user.rol === 'conserje' ? "/fichajes" : "/dashboard") : "/login"} />} />
          <Route path="*" element={<Navigate to={user ? (user.rol === 'conserje' ? "/fichajes" : "/dashboard") : "/login"} />} />
        </Routes>
          </div>
        </ErrorBoundary>
      </div>
    </div>
  );
}

export default App;
